import { useState } from "react";
import { IconAlertTriangle, IconCheck, IconTrash } from "@tabler/icons-react";
import { Alert, Button, Checkbox, Divider, Group, Stack, Text } from "@/components/ui";
import { useRolesAsignables } from "../hooks/use-roles-asignables";
import { useActualizarRolesNivel, useEliminarNivel } from "../hooks/use-niveles-mutations";
import type { NivelAutorizacion } from "../services/niveles-service";

/**
 * Cuerpo del modal para editar los roles de un nivel (o eliminarlo). Se
 * renderiza dentro de un <Dialog> controlado por la matriz. `onCerrar` cierra
 * ese diálogo al terminar. El borrado se confirma inline (no abre otro modal).
 */
export function NivelEditarModalBody({
  nivel,
  contexto,
  onCerrar,
}: {
  nivel: NivelAutorizacion;
  /** Texto tipo "FB60 / CFDI_UNICO · Tesorería" para orientar al usuario. */
  contexto: string;
  onCerrar: () => void;
}) {
  const [rolesSel, setRolesSel] = useState<Set<string>>(new Set(nivel.rolesAutorizadores));
  const [generaPoliza, setGeneraPoliza] = useState(nivel.generaPolizaAlAprobar);
  const [confirmandoBaja, setConfirmandoBaja] = useState(false);
  const rolesAsignables = useRolesAsignables();
  const guardar = useActualizarRolesNivel({ onActualizado: onCerrar });
  const eliminar = useEliminarNivel();

  const toggleRol = (r: string) => {
    const next = new Set(rolesSel);
    if (next.has(r)) next.delete(r);
    else next.add(r);
    setRolesSel(next);
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {contexto} · <strong>Nivel {nivel.nivel}</strong>
      </Text>
      <div>
        <Text size="xs" c="dimmed" fw={600} className="mb-2 uppercase tracking-wide">
          Roles autorizadores
        </Text>
        <Group gap="md" className="flex-wrap">
          {/* Unión del catálogo de roles + los ya asignados al nivel (por si alguno
              fuera un rol viejo que ya no está en el catálogo). */}
          {Array.from(new Set([...rolesAsignables, ...nivel.rolesAutorizadores])).map((r) => (
            <Checkbox key={r} label={r} checked={rolesSel.has(r)} onCheckedChange={() => toggleRol(r)} />
          ))}
        </Group>
      </div>

      <div>
        <Text size="xs" c="dimmed" fw={600} className="mb-2 uppercase tracking-wide">
          Generación de póliza
        </Text>
        <Checkbox
          label="Generar la póliza TXT al aprobar este nivel"
          checked={generaPoliza}
          onCheckedChange={(v) => setGeneraPoliza(Boolean(v))}
        />
        <Text size="xs" c="dimmed" className="mt-1">
          Por defecto la póliza se genera al enviar a autorización. Márcalo para diferir la
          generación hasta que este nivel apruebe. Para aplicarlo a todas las áreas de una vez, usa
          el interruptor del encabezado del nivel en la matriz.
        </Text>
      </div>

      {confirmandoBaja ? (
        <Alert variant="error" icon={<IconAlertTriangle size={16} />}>
          <Stack gap="xs">
            <Text size="sm">
              ¿Eliminar el nivel {nivel.nivel}? Las solicitudes ya en autorización <strong>NO</strong> se afectan (el
              flujo ya se congeló). Solo afecta a las nuevas.
            </Text>
            <Group gap="xs">
              <Button
                size="xs"
                variant="filled"
                className="bg-peligro-600 hover:bg-peligro-700"
                leftSection={<IconTrash size={14} />}
                loading={eliminar.isPending}
                onClick={() => eliminar.mutate({ id: nivel.id, nivel: nivel.nivel }, { onSuccess: onCerrar })}
              >
                Sí, eliminar
              </Button>
              <Button size="xs" variant="default" onClick={() => setConfirmandoBaja(false)}>
                Cancelar
              </Button>
            </Group>
          </Stack>
        </Alert>
      ) : null}

      <Divider />
      <Group justify="space-between">
        <Button
          variant="subtle"
          className="text-peligro-600 hover:bg-peligro-50 dark:hover:bg-peligro-900/40"
          leftSection={<IconTrash size={14} />}
          disabled={confirmandoBaja}
          onClick={() => setConfirmandoBaja(true)}
        >
          Eliminar nivel
        </Button>
        <Group gap="xs">
          <Button variant="default" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            leftSection={<IconCheck size={14} />}
            loading={guardar.isPending}
            disabled={rolesSel.size === 0}
            onClick={() =>
              guardar.mutate({
                id: nivel.id,
                roles: Array.from(rolesSel),
                generaPolizaAlAprobar: generaPoliza,
              })
            }
          >
            Guardar
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
